/** Pure helpers behind `ui/EntityPicker` (P-41, D-203/D-207). No React, unit-tested. */
import { normalizeForSearch } from './customerPickerModel';

export interface PickerOption {
  value: string;
  label: string;
  /** Grey second line (e.g. "לקוח לא פעיל", an email, a type). Also searched. */
  secondary?: string;
  /** Extra text that matches the search but is not shown (tax id, email, ...). */
  keywords?: string;
  disabled?: boolean;
  /** Indent level (milestone trees). */
  depth?: number;
  /** Group heading shown above the first row of the group (no filtering by it). */
  group?: string;
}

export const EMPTY_VALUE = '';

/** Rank of `option` for `query` (lower is better), or -1 when it does not match. */
export function rankOption(option: PickerOption, q: string, words: readonly string[]): number {
  const name = normalizeForSearch(option.label);
  if (name.startsWith(q)) return 0;
  if (name.split(' ').some((w) => w.startsWith(q))) return 1;
  if (words.every((w) => name.includes(w))) return 2;
  const extra = normalizeForSearch(`${option.secondary ?? ''} ${option.keywords ?? ''}`);
  if (extra && words.every((w) => extra.includes(w) || name.includes(w))) return 3;
  const digits = q.replace(/\D/g, '');
  if (digits && /\d/.test(q) && option.keywords && option.keywords.replace(/\D/g, '').includes(digits)) return 3;
  return -1;
}

/**
 * Options matching `query`. An empty query keeps the given order (the caller
 * sorts: trees, groups). A query ranks prefix matches first, then word-contains,
 * then secondary / keyword matches; ties keep the given order. At most `limit`
 * rows plus the total match count.
 */
export function filterOptions<T extends PickerOption>(list: readonly T[], query: string, limit = 200): { rows: T[]; total: number } {
  const q = normalizeForSearch(query);
  if (!q) return { rows: list.slice(0, limit), total: list.length };
  const words = q.split(' ');
  const scored: { o: T; rank: number; i: number }[] = [];
  list.forEach((o, i) => {
    const rank = rankOption(o, q, words);
    if (rank >= 0) scored.push({ o, rank, i });
  });
  scored.sort((a, b) => a.rank - b.rank || a.i - b.i);
  return { rows: scored.slice(0, limit).map((s) => s.o), total: scored.length };
}

/** The next enabled index from `from` in `dir` (no wrap), or `from` when none. */
export function moveActive(rows: readonly PickerOption[], from: number, dir: 1 | -1): number {
  for (let i = from + dir; i >= 0 && i < rows.length; i += dir) if (!rows[i].disabled) return i;
  return from;
}

/** First / last enabled index. */
export function edgeActive(rows: readonly PickerOption[], end: boolean): number {
  if (end) for (let i = rows.length - 1; i >= 0; i--) if (!rows[i].disabled) return i;
  if (!end) for (let i = 0; i < rows.length; i++) if (!rows[i].disabled) return i;
  return 0;
}

/** Toggle `value` in a multi selection, keeping order of insertion. */
export function toggleValue(values: readonly string[], value: string): string[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

/** Label for a stored value: the option's label, or `fallback` when unknown. */
export function labelFor(options: readonly PickerOption[], value: string, fallback = ''): string {
  return options.find((o) => o.value === value)?.label ?? fallback;
}

/** Keep a stored value visible when it is not in the (filtered / truncated) list. */
export function withCurrent(options: readonly PickerOption[], value: string, label: string): PickerOption[] {
  if (!value || options.some((o) => o.value === value)) return options as PickerOption[];
  return [{ value, label: label || '—' }, ...options];
}

/**
 * For search results of a tree (milestones): drop the indent and show the
 * ancestors' path as the grey second line, so a child never appears without
 * its parent. `all` is the full list in tree order (parents before children).
 */
export function withParentPaths<T extends PickerOption>(rows: readonly T[], all: readonly T[]): T[] {
  if (!rows.some((r) => r.depth)) return rows as T[];
  const path = new Map<string, string>();
  const stack: string[] = [];
  for (const o of all) {
    const d = o.depth ?? 0;
    stack.length = Math.min(stack.length, d);
    if (d > 0 && stack.length) path.set(o.value, stack.join(' › '));
    stack[d] = o.label;
  }
  return rows.map((r) => {
    const p = path.get(r.value);
    return p ? { ...r, depth: 0, secondary: r.secondary ? `${p} · ${r.secondary}` : p } : r;
  });
}
